import { useIsLargeScreen } from "apps/portal/src/hooks";
import { ModalWrapper, useClubSettings } from "apps/portal/src/providers";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { Button, Popover, PopoverContent, PopoverTrigger } from "../../atoms";
import DatePickerNew from "../../atoms/base/DatePickerNew/DatePickerNew";
import { CalendarDateIcon } from "../../atoms/icons/CalendarDateIcon";

export type DateSelectionRange = { startDate: string; endDate: string };

type RangeDatePickerProps = {
  onDateSelectionApply: (range: DateRange | undefined) => void;
  dateRange?: DateRange;
};

const RangeDatePicker = ({
  onDateSelectionApply,
  dateRange,
}: RangeDatePickerProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { club } = useClubSettings();

  const isLargerScreen = useIsLargeScreen(1023);
  const todayLocal = DateTime.now().setZone(club.timezone);

  const today = todayLocal.toJSDate();
  const { t } = useTranslation();

  const defaultRange: DateRange = {
    from: todayLocal.minus({ days: 6 }).toJSDate(),
    to: today,
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [range, setRange] = useState(defaultRange);
  const [initialRange, setInitialRange] = useState(defaultRange);

  const [displayMonth, setDisplayMonth] = useState(defaultRange.from);

  const yesterday = todayLocal.minus({ days: 1 }).toJSDate();
  const monthStart = todayLocal.startOf("month").toJSDate();
  const quarterStart = todayLocal.startOf("quarter").toJSDate();
  const yearStart = todayLocal.startOf("year").toJSDate();

  useEffect(() => {
    if (dateRange && dateRange?.from && dateRange?.to) {
      setRange(dateRange);
      setInitialRange(dateRange);
      setDisplayMonth(dateRange.from);
    }
  }, [dateRange]);

  const predefinedRanges = [
    {
      label: t("components.dayPicker.range.today"),
      range: {
        from: today,
        to: today,
      },
    },
    {
      label: t("components.dayPicker.range.yesterday"),
      range: {
        from: yesterday,
        to: yesterday,
      },
    },
    {
      label: t("components.dayPicker.range.lastSevenDays"),
      range: {
        from: todayLocal.minus({ days: 6 }).toJSDate(),
        to: today,
      },
    },
    {
      label: t("components.dayPicker.range.lastFourWeeks"),
      range: {
        from: todayLocal.minus({ weeks: 4 }).toJSDate(),
        to: today,
      },
    },
    {
      label: t("components.dayPicker.range.monthToDate"),
      range: {
        from: monthStart,
        to: today,
      },
    },
    {
      label: t("components.dayPicker.range.quarterToDate"),
      range: {
        from: quarterStart,
        to: today,
      },
    },
    {
      label: t("components.dayPicker.range.yearToDate"),
      range: {
        from: yearStart,
        to: today,
      },
    },
  ];

  const getRangeLabel = () => {
    const match = predefinedRanges.find(
      (item) =>
        range?.from &&
        range?.to &&
        DateTime.fromJSDate(range.from).hasSame(
          DateTime.fromJSDate(item.range.from),
          "day",
        ) &&
        DateTime.fromJSDate(range.to).hasSame(
          DateTime.fromJSDate(item.range.to),
          "day",
        ),
    );

    if (match) return match.label;

    if (range?.from && range?.to) {
      const fromLabel = DateTime.fromJSDate(range.from).toLocaleString(
        DateTime.DATE_MED,
      );
      const toLabel = DateTime.fromJSDate(range.to).toLocaleString(
        DateTime.DATE_MED,
      );

      return `${fromLabel} – ${toLabel}`;
    }

    return t("components.dayPicker.selectRange");
  };

  const applyRange = (newRange: DateRange) => {
    if (newRange?.from && newRange?.to) {
      setRange(newRange);
      setDisplayMonth(newRange.from);
    }
  };

  const onApply = () => {
    setRange((currentRange) => {
      if (currentRange?.from && currentRange?.to) {
        onDateSelectionApply(currentRange);
        setDisplayMonth(currentRange.from);
      }
      return currentRange;
    });

    if (!isLargerScreen) {
      setModalOpen(false);
    } else {
      setPopoverOpen(false);
    }
  };

  const onSelect = (selected?: DateRange) => {
    if (!selected) return;

    setRange(() => {
      if (selected?.from && selected?.to) {
        setDisplayMonth(selected.from);
      }
      return selected;
    });
  };

  const onCancel = () => {
    const fallbackRange = initialRange ?? defaultRange;

    setRange(() => {
      if (fallbackRange?.from) {
        setDisplayMonth(fallbackRange.from);
      }
      return fallbackRange;
    });

    if (!isLargerScreen) {
      setModalOpen(false);
    } else {
      setPopoverOpen(false);
    }
  };

  const onRangeDatePickerButtonClick = () => {
    if (!isLargerScreen) {
      setModalOpen(true);
    } else {
      setPopoverOpen((prev) => !prev);
    }
  };

  const datePicker = (
    <DatePickerNew
      displayMonth={displayMonth}
      onMonthChange={setDisplayMonth}
      range={range}
      onSelect={onSelect}
      predefinedRanges={predefinedRanges}
      applyRange={applyRange}
      onCancel={onCancel}
      onApply={onApply}
    />
  );

  return (
    <Popover onOpenChange={(open) => setPopoverOpen(open)} open={popoverOpen}>
      <PopoverTrigger className="bg-gray-0 w-fit focus:outline-none" asChild>
        <Button
          intent="default"
          className="flex min-w-[14.375rem] justify-around whitespace-nowrap rounded-[1.25rem] p-0 font-semibold"
          onClick={onRangeDatePickerButtonClick}
        >
          <div className="flex w-full justify-around">
            <div className="flex h-full items-center border-r border-gray-200 p-2 font-medium">
              {t("components.dayPicker.show")}
            </div>
            <div className="flex min-w-60 items-center justify-center">
              <CalendarDateIcon
                pathClassName="stroke-gray-950"
                className="mr-1 text-gray-950"
              />
              {getRangeLabel()}
            </div>
          </div>
        </Button>
      </PopoverTrigger>
      {!isLargerScreen && modalOpen && (
        <ModalWrapper
          overlayClassName="max-lg:justify-center sm:p-0"
          className="bg-transparent !p-0"
          onCancel={() => setModalOpen(false)}
        >
          {datePicker}
        </ModalWrapper>
      )}
      {isLargerScreen && popoverOpen && (
        <PopoverContent
          align="end"
          sideOffset={8}
          avoidCollisions
          collisionPadding={8}
          className="w-fit max-w-[calc(100vw-1rem)]"
        >
          {datePicker}
        </PopoverContent>
      )}
    </Popover>
  );
};

export default RangeDatePicker;
